import React, { useCallback } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';

import type { RemixContentComponentProps } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import { RemixContainer } from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';

import { useRemixToAIFC } from './useRemixToAIFC.ts';

export type DynamicUiSource = {
	contentType: 'page';
	startLocalId: string;
	endLocalId: string;
	startIndex: number;
	endIndex: number;
	fragmentAdf: DocNode;
	fullAdf: DocNode;
};

/**
 * Creates a content component for RemixContentComponent using RemixContainer.
 */
export const createRemixContentComponent = () => {
	const Component = ({
		handleModalClose,
		selectedContext,
		editorApi,
	}: RemixContentComponentProps) => {
		const { openAIFCStagingWithPrompt } = useRemixToAIFC();

		const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
			const localIds = selectedContext?.localIds ?? [];
			const startLocalId = localIds[0] ?? '';
			const endLocalId = localIds[localIds.length - 1] ?? '';

			const fragmentAdf: DocNode = {
				type: 'doc',
				version: 1,
				content: (selectedContext?.selectionFragment as DocNode['content']) ?? [],
			};

			let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
			if (editorApi) {
				fullAdf = await new Promise((resolve) => {
					try {
						editorApi.core?.actions?.requestDocument?.(
							(doc: any) => {
								resolve(doc as DocNode);
							},
							{ alwaysFire: true },
						);
					} catch {
						resolve({ type: 'doc', version: 1, content: [] });
					}
				});
			}

			return {
				contentType: 'page' as const,
				startLocalId,
				endLocalId,
				// TODO: SelectionContext will eventually have these values
				startIndex: 0,
				endIndex: -1,
				fragmentAdf,
				fullAdf,
			};
		}, [editorApi, selectedContext]);

		const onOptionSelect = useCallback(
			async (type: string) => {
				handleModalClose();
				const dynamicUiSource = await constructDynamicUiSource();
				void openAIFCStagingWithPrompt(
					selectedContext.selectionMarkdown || '',
					type,
					dynamicUiSource,
				);
			},
			[
				handleModalClose,
				openAIFCStagingWithPrompt,
				selectedContext.selectionMarkdown,
				constructDynamicUiSource,
			],
		);

		return (
			<RemixContainer
				selectedText={selectedContext.selectionMarkdown || ''}
				onClose={handleModalClose}
				onOptionSelect={onOptionSelect}
			/>
		);
	};

	const Wrapper = (props: RemixContentComponentProps) => {
		return props.isModalOpen ? (
			<ConfluenceConversationAssistant>
				<Component {...props} />
			</ConfluenceConversationAssistant>
		) : null;
	};

	return Wrapper;
};
